import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

import RoleTypeService from '../../../services/roleTypeService';
import { useAuth } from '../../../contexts/AuthContext';

const EditRoleTypesModal = ({ isOpen, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  
  const [roleTypeOptions, setRoleTypeOptions] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('voice_type');
  const [editingItem, setEditingItem] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  
  const [formData, setFormData] = useState({
    type_name: '',
    display_label: '',
    category: 'voice_type',
    sort_order: 0
  });

  const categories = [
    { value: 'voice_type', label: 'Voice Types' },
    { value: 'role_type', label: 'Role Types' },
    { value: 'age_range', label: 'Age Ranges' },
    { value: 'vocal_range', label: 'Vocal Range' }
  ];

  useEffect(() => {
    if (isOpen && user?.id) {
      checkAdminStatus();
      loadRoleTypeOptions();
    }
  }, [isOpen, user?.id]);

  const checkAdminStatus = async () => {
    try {
      const { data, error, method, warning } = await RoleTypeService?.isUserAdmin(user?.id);
      
      setIsAdmin(data || false);
      
      if (!data) {
        const errorMessage = warning 
          ? `${warning}. You need admin or editor privileges to manage role types.`
          : 'You do not have permission to manage role types. Contact your administrator for access.';
        setError(errorMessage);
      }

      if (error) {
        console.warn('Admin check error:', error);
      }
    } catch (err) {
      setIsAdmin(false);
      setError('Unable to verify admin permissions. Please refresh the page and try again.');
      console.error('Error checking admin status:', err);
    }
  };

  const loadRoleTypeOptions = async () => {
    setLoading(true);
    try {
      const { data, error } = await RoleTypeService?.getAllRoleTypeOptions();
      if (error) {
        setError('Failed to load role type options');
      } else {
        setRoleTypeOptions(data || []);
      }
    } catch (err) {
      setError('Failed to load role type options');
    } finally {
      setLoading(false);
    }
  };

  const filteredOptions = roleTypeOptions?.filter(option => 
    option?.category === selectedCategory
  ) || [];

  const handleInputChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAdd = () => {
    setEditingItem(null);
    setFormData({
      type_name: '',
      display_label: '',
      category: selectedCategory,
      sort_order: (filteredOptions?.length || 0) + 1
    });
    setShowAddForm(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      type_name: item?.type_name || '',
      display_label: item?.display_label || '',
      category: item?.category || selectedCategory,
      sort_order: item?.sort_order || 0
    });
    setShowAddForm(true);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!formData?.type_name?.trim() || !formData?.display_label?.trim()) {
      setError('Type name and display label are required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let result;
      if (editingItem) {
        result = await RoleTypeService?.updateRoleTypeOption(
          editingItem?.id,
          formData,
          user?.id
        );
      } else {
        result = await RoleTypeService?.createRoleTypeOption(formData, user?.id);
      }

      if (result?.error) {
        setError(result?.error?.message || `Failed to ${editingItem ? 'update' : 'create'} option`);
      } else {
        setSuccess(true);
        setShowAddForm(false);
        setTimeout(() => {
          setSuccess(false);
          loadRoleTypeOptions();
        }, 2000);
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Are you sure you want to delete "${item?.display_label}"?`)) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { error } = await RoleTypeService?.deleteRoleTypeOption(item?.id);
      if (error) {
        setError(error?.message || 'Failed to delete option');
      } else {
        setSuccess(true);
        setTimeout(() => {
          setSuccess(false);
          loadRoleTypeOptions();
        }, 2000);
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setShowAddForm(false);
      setEditingItem(null);
      onClose?.();
    }
  };

  if (!isOpen) return null;

  if (!isAdmin) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
        <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl max-w-md w-full p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-white">Access Restricted</h2>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
            >
              <Icon name="X" size={20} className="text-blue-100" />
            </button>
          </div>
          <div className="flex items-center space-x-3 p-4 bg-red-600 bg-opacity-20 border border-red-400 border-opacity-30 rounded-lg">
            <Icon name="AlertTriangle" size={20} className="text-red-400" />
            <div>
              <p className="font-medium text-red-100">Permission Required</p>
              <p className="text-sm text-red-200">
                {error || 'You need admin or editor privileges to manage role types.'}
              </p>
            </div>
          </div>
          <div className="mt-4 text-center">
            <p className="text-sm text-blue-200">
              Contact your system administrator to request access to role type management features.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white border-opacity-20">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg flex items-center justify-center">
              <Icon name="Settings" size={20} className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">Edit Role Types</h2>
              <p className="text-sm text-blue-100">Manage voice types, role types, and ranges</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
          >
            <Icon name="X" size={20} className="text-blue-100" />
          </button>
        </div>

        {/* Success State */}
        {success && (
          <div className="p-6 border-b border-white border-opacity-20">
            <div className="flex items-center space-x-3 p-4 bg-green-600 bg-opacity-20 border border-green-400 border-opacity-30 rounded-lg">
              <Icon name="CheckCircle" size={20} className="text-green-400" />
              <div>
                <p className="font-medium text-green-100">Success!</p>
                <p className="text-sm text-green-200">Role type options have been updated.</p>
              </div>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="p-6 border-b border-white border-opacity-20">
            <div className="flex items-center space-x-3 p-4 bg-red-600 bg-opacity-20 border border-red-400 border-opacity-30 rounded-lg">
              <Icon name="AlertTriangle" size={20} className="text-red-400" />
              <div>
                <p className="font-medium text-red-100">Error</p>
                <p className="text-sm text-red-200">{error}</p>
              </div>
            </div>
          </div>
        )}

        <div className="flex h-[calc(90vh-200px)]">
          {/* Category Selector */}
          <div className="w-1/3 p-6 border-r border-white border-opacity-20 overflow-y-auto">
            <h3 className="text-lg font-medium text-white mb-4">Categories</h3>
            <div className="space-y-2">
              {categories?.map(category => (
                <button
                  key={category?.value}
                  onClick={() => {
                    setSelectedCategory(category?.value);
                    setShowAddForm(false);
                    setEditingItem(null);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                    selectedCategory === category?.value
                      ? 'bg-indigo-600 text-white' :'text-blue-100 hover:bg-white hover:bg-opacity-10'
                  }`}
                >
                  {category?.label}
                </button>
              ))}
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 p-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-white">
                {categories?.find(c => c?.value === selectedCategory)?.label || 'Options'}
              </h3>
              <Button
                onClick={handleAdd}
                iconName="Plus"
                iconPosition="left"
                className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white border-0"
              >
                Add New
              </Button>
            </div>

            {/* Add/Edit Form */}
            {showAddForm && (
              <div className="mb-6 p-4 bg-white bg-opacity-10 border border-white border-opacity-30 rounded-lg">
                <h4 className="text-white font-medium mb-4">
                  {editingItem ? 'Edit Option' : 'Add New Option'}
                </h4>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-blue-100 mb-2">
                        Type Name *
                      </label>
                      <Input
                        type="text"
                        value={formData?.type_name}
                        onChange={(e) => handleInputChange('type_name', e?.target?.value)}
                        placeholder="e.g., soprano, lead, etc."
                        required
                        disabled={loading}
                        className="w-full bg-white bg-opacity-10 border-white border-opacity-30 text-white placeholder:text-blue-200"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-blue-100 mb-2">
                        Display Label *
                      </label>
                      <Input
                        type="text"
                        value={formData?.display_label}
                        onChange={(e) => handleInputChange('display_label', e?.target?.value)}
                        placeholder="e.g., Soprano, Lead, etc."
                        required
                        disabled={loading}
                        className="w-full bg-white bg-opacity-10 border-white border-opacity-30 text-white placeholder:text-blue-200"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-blue-100 mb-2">
                      Sort Order
                    </label>
                    <Input
                      type="number"
                      value={formData?.sort_order}
                      onChange={(e) => handleInputChange('sort_order', parseInt(e?.target?.value) || 0)}
                      disabled={loading}
                      className="w-full bg-white bg-opacity-10 border-white border-opacity-30 text-white"
                    />
                  </div>
                  <div className="flex items-center justify-end space-x-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowAddForm(false)}
                      disabled={loading}
                      className="border-white border-opacity-30 text-blue-100 hover:bg-white hover:bg-opacity-20"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white border-0"
                    >
                      {editingItem ? 'Update' : 'Add'}
                    </Button>
                  </div>
                </form>
              </div>
            )}

            {/* Options List */}
            <div className="space-y-2">
              {filteredOptions?.length === 0 ? (
                <div className="text-center py-8">
                  <Icon name="Inbox" size={48} className="text-blue-200 mx-auto mb-4" />
                  <p className="text-blue-100">No options found for this category.</p>
                </div>
              ) : (
                filteredOptions?.map((option) => (
                  <div
                    key={option?.id}
                    className="flex items-center justify-between p-4 bg-white bg-opacity-10 border border-white border-opacity-30 rounded-lg hover:bg-opacity-20 transition-colors"
                  >
                    <div>
                      <h4 className="font-medium text-white">{option?.display_label}</h4>
                      <p className="text-sm text-blue-200">
                        Type: {option?.type_name} • Order: {option?.sort_order}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(option)}
                        iconName="Edit2"
                        className="border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white"
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(option)}
                        iconName="Trash2"
                        className="border-red-400 text-red-400 hover:bg-red-400 hover:text-white"
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditRoleTypesModal;